import { z } from "zod";
import { therapistCodeSchema } from "./therapist-code.ts";

const uuidSchema = z.string().uuid();
const offsetDateTimeSchema = z.string().datetime({ offset: true });

/** The two availability engines have different time horizons and inputs. */
export const capacityBookingKindSchema = z.enum(["asap", "scheduled"]);

const bookingRequirementsSchema = z
  .object({
    serviceVariantIds: z.array(uuidSchema).min(1),
    /** Server-derived request cardinality; this contract sets no allocation limit. */
    clientCount: z.number().int().positive(),
  })
  .strict();

/** A half-open instant range, shared by appointment, duty, and service windows. */
export const capacityTimeWindowSchema = z
  .object({
    startsAt: offsetDateTimeSchema,
    endsAt: offsetDateTimeSchema,
  })
  .strict()
  .superRefine((window, context) => {
    if (Date.parse(window.endsAt) <= Date.parse(window.startsAt)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["endsAt"],
        message: "Window end must be after start",
      });
    }
  });

const scheduledAppointmentWindowSchema = capacityTimeWindowSchema;

/**
 * Server-side evaluation input. It deliberately has neither client PII nor
 * address/GPS data: location and policy evaluation stay behind the service.
 */
export const capacityEvaluationInputSchema = z.discriminatedUnion("bookingKind", [
  z
    .object({
      bookingKind: z.literal("asap"),
      hubId: uuidSchema,
      requirements: bookingRequirementsSchema,
    })
    .strict(),
  z
    .object({
      bookingKind: z.literal("scheduled"),
      hubId: uuidSchema,
      appointmentWindow: scheduledAppointmentWindowSchema,
      requirements: bookingRequirementsSchema,
    })
    .strict(),
]);

/**
 * The request-level facts an evaluation is made against. `now` is the SERVER
 * clock — a client-supplied instant would let a caller walk past the lead-time
 * and operating-hour rules.
 */
export const capacityEvaluationContextSchema = z
  .object({
    bookingKind: capacityBookingKindSchema,
    hubId: uuidSchema,
    now: offsetDateTimeSchema,
    /**
     * The therapist-time window the session would occupy. ASAP callers derive
     * it from the current instant plus combined duration; Scheduled callers
     * pass the selected appointment window.
     */
    serviceWindow: capacityTimeWindowSchema,
    /**
     * `operating_settings.service_area_limit_km` (0330), read by the caller.
     * Absent means the approved 15 km (`SERVICE_AREA_LIMIT_KM`).
     */
    serviceAreaLimitKm: z.number().positive().optional(),
  })
  .strict();

/**
 * Whether a therapist may perform the requested services. There is no
 * therapist-to-service qualification table in the schema today, so the absent
 * case is explicit: it must surface as a blocker, never be inferred from role,
 * name, or booking history (CAP-02 risk note).
 */
export const therapistQualificationSignalSchema = z.enum([
  "qualified",
  "unqualified",
  "source_unavailable",
]);

/**
 * Inputs to a therapist eligibility evaluator. Every field is a server-derived
 * FACT; the rules applied to them (duty coverage, service-area ceiling) live in
 * `@bednbody/logic`. Ranking, favorites, Therapist Code, and allocation are
 * deliberately absent — they are caller policy, not eligibility.
 */
export const therapistEligibilityInputSchema = z
  .object({
    therapistId: uuidSchema,
    hubId: uuidSchema,
    /** `staff.active` — the only staff-state signal that exists (OQ-04). */
    isActive: z.boolean(),
    /** A published schedule version covers the evaluated date (SCH-05). */
    hasPublishedSchedule: z.boolean(),
    /** Published `duty` entries only. Rest days are absent, never a zero window. */
    dutyWindows: z.array(capacityTimeWindowSchema),
    /** Overlap with a protected commitment, resolved server-side. */
    hasConflictingCommitment: z.boolean(),
    qualification: therapistQualificationSignalSchema,
    /** Server-computed hub→client road distance; null when no estimate exists. */
    hubToClientKm: z.number().nonnegative().finite().nullable(),
  })
  .strict();

/** Stable, PII-free reasons that a capacity assessment may expose. */
export const capacityBlockerCodeSchema = z.enum([
  "STAFF_INACTIVE",
  "OFF_DUTY",
  "UNQUALIFIED",
  /** No trusted qualification source exists — distinct from "not qualified". */
  "QUALIFICATION_SOURCE_UNAVAILABLE",
  "CONFLICTING_COMMITMENT",
  "SCHEDULE_UNPUBLISHED",
  "SCHEDULE_CONFLICT",
  "SERVICE_AREA_INELIGIBLE",
  /** No road-distance estimate — never silently treated as in-area. */
  "SERVICE_DISTANCE_UNAVAILABLE",
  /**
   * ASAP only: the therapist has reported no live position, so ASAP cannot say
   * where they are. Answers OQ-47 option (a) — dropped from candidacy until a
   * pin exists, rather than sorting last forever with nothing on screen to say
   * why. Distinct from SERVICE_DISTANCE_UNAVAILABLE, which means we HAVE a pin
   * and the routing provider failed to measure from it.
   */
  "LOCATION_UNKNOWN",
  /**
   * ASAP only: no open `attendance` row. `pick_therapists` (0017/0049) has
   * always required one; the ASAP engine did not, so the two paths disagreed
   * about whether a clocked-out therapist may be dispatched.
   */
  "NOT_CLOCKED_IN",
  /**
   * ASAP only: clocked in, but `staff.last_seen_at` is outside the 90-second
   * presence window (0049) — the app is closed or backgrounded.
   *
   * DELIBERATELY DISTINCT FROM `NOT_CLOCKED_IN`, because the remedy differs:
   * this one is "open the app", that one is "clock in". Collapsing them sends
   * the therapist to the wrong fix — the same mistake the clock-in error
   * mapping made when a home-approval failure reported as a hub problem.
   */
  "PRESENCE_STALE",
  /** Request window falls outside 2:00 PM–12:00 AM Asia/Manila (OQ-05). */
  "OUTSIDE_OPERATING_HOURS",
  /** Same-day Scheduled request inside the two-hour lead time (OQ-06). */
  "LEAD_TIME_NOT_MET",
  "THERAPIST_UNAVAILABLE",
  "NO_ELIGIBLE_THERAPIST",
  "NO_COMPLETE_RIDER_PLAN",
  "ROUTE_PLAN_UNAVAILABLE",
  /** Provider gave no usable road estimate — never a straight-line fallback (OQ-14). */
  "ROUTING_UNAVAILABLE",
  /** A required pickup or drop-off movement is missing from the plan. */
  "INCOMPLETE_MOVEMENTS",
  /** The plan never returns the therapist to a hub or hands them on directly. */
  "NO_RETURN_PLAN",
  /** Legs do not join up, overlap each other, or under-run their road estimate. */
  "ROUTE_DISCONTINUITY",
  /** The rider is already committed across part of this plan. */
  "RIDER_COMMITMENT_CONFLICT",
  /** Arrival lands after the session starts, or collection before it ends. */
  "SESSION_WINDOW_UNPROTECTED",
  /** Rider belongs to another hub; cross-hub sharing is deferred (OQ-13). */
  "CROSS_HUB_UNSUPPORTED",
  "CAPACITY_CHANGED",
  "ADVISORY_STALE",
  "BOOKING_KIND_UNSUPPORTED",
]);

export const capacityBlockerSubjectSchema = z.enum(["therapist", "rider", "capacity", "advisory"]);

export const capacityBlockerSchema = z
  .object({
    code: capacityBlockerCodeSchema,
    subject: capacityBlockerSubjectSchema,
    staffId: uuidSchema.optional(),
    hubId: uuidSchema.optional(),
  })
  .strict();

/** Eligibility is a therapist-level finding, not a claim that a slot is bookable. */
export const therapistEligibilityResultSchema = z
  .object({
    therapistId: uuidSchema,
    eligible: z.boolean(),
    blockers: z.array(capacityBlockerSchema),
  })
  .strict()
  .superRefine((result, context) => {
    if (result.eligible && result.blockers.length > 0) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["blockers"],
        message: "Eligible therapists cannot have blockers",
      });
    }
    if (!result.eligible && result.blockers.length === 0) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["blockers"],
        message: "Ineligible therapists require at least one blocker",
      });
    }
  });

/**
 * A route-plan summary contains staff references and versioning only. Route
 * geometry, address, live location, and operational policy stay server-side.
 */
export const capacityPlanSummarySchema = z
  .object({
    bookingKind: capacityBookingKindSchema,
    planToken: z.string().min(1).max(200),
    planVersion: z.string().min(1).max(200),
    therapistIds: z.array(uuidSchema),
    riderIds: z.array(uuidSchema),
    complete: z.boolean(),
    blockers: z.array(capacityBlockerSchema),
  })
  .strict()
  .superRefine((plan, context) => {
    if (plan.complete && plan.blockers.length > 0) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["blockers"],
        message: "Complete plans cannot have blockers",
      });
    }
    if (!plan.complete && plan.blockers.length === 0) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["blockers"],
        message: "Incomplete plans require at least one blocker",
      });
    }
  });

/**
 * Advisory result only: `bookable` is a current assessment, never a capacity
 * reservation or payment confirmation. The booking transaction must recheck it.
 */
export const advisoryAvailabilityResultSchema = z
  .object({
    bookingKind: capacityBookingKindSchema,
    evaluatedAt: offsetDateTimeSchema,
    advisoryToken: z.string().min(1).max(200),
    capacityVersion: z.string().min(1).max(200),
    therapistEligibility: z.array(therapistEligibilityResultSchema),
    planSummaries: z.array(capacityPlanSummarySchema),
    hasEligibleTherapist: z.boolean(),
    hasCompletePlan: z.boolean(),
    bookable: z.boolean(),
    blockers: z.array(capacityBlockerSchema),
  })
  .strict()
  .superRefine((result, context) => {
    const hasEligibleTherapist = result.therapistEligibility.some(
      (therapist) => therapist.eligible,
    );
    const hasCompletePlan = result.planSummaries.some((plan) => plan.complete);

    if (result.hasEligibleTherapist !== hasEligibleTherapist) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["hasEligibleTherapist"],
        message: "Must match therapist eligibility results",
      });
    }
    if (result.hasCompletePlan !== hasCompletePlan) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["hasCompletePlan"],
        message: "Must match plan summaries",
      });
    }
    if (result.bookable && (!result.hasEligibleTherapist || !result.hasCompletePlan)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["bookable"],
        message: "Bookable capacity requires an eligible therapist and complete plan",
      });
    }
  });

/* ── Scheduled availability read (SBOOK-01, Scheduled Flows 1, 2, 2A, 2B, 3) ── */

const manilaDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Expected a YYYY-MM-DD date");

/**
 * What the client asks for before picking a date. Flow 1 collects clients,
 * services and the service address; the address arrives as a MAP PIN because
 * Flow 2B's 15 km test and Flow 11's travel table are both stated in ROAD
 * distance, and a road distance needs two coordinates. No address text, no
 * saved-address id, no client identity — the caller is resolved from the JWT.
 */
export const scheduledAvailabilityRequestSchema = z
  .object({
    // No hubId — server-derived from `serviceLocation` (0056), same rule as the
    // ASAP contract above. Scheduled matters MORE here, not less: it allocates
    // against published rota and rider plans, so a client-named hub would pick
    // which roster it is measured against, days ahead.
    serviceVariantIds: z.array(uuidSchema).min(1),
    /** Flow 1: a booking carries 1–4 clients. */
    clientCount: z.number().int().min(1).max(4),
    serviceLocation: z
      .object({
        lat: z.number().min(-90).max(90),
        lng: z.number().min(-180).max(180),
      })
      .strict(),
  })
  .strict();

/** ASAP Flow 3's four Booking Options. */
export const asapBookingOptionSchema = z.enum([
  "favorites_only",
  "favorites_first",
  "therapist_code",
  "earliest_available",
]);

/**
 * ASAP availability request (Flows 3–6A). `clientId` is deliberately absent —
 * the Edge Function resolves it from the verified JWT, never the body
 * (rules/security.md). So is the Therapist Code itself: ASAP-06 validates the
 * code and this carries the therapist it resolved to.
 *
 * ═══ AND SO IS `hubId`, AS OF 0056 ═══
 *
 * It used to be here and the server used what it was given. The hub decides
 * which therapist pool and which earnings queue a booking is allocated from, so
 * a client naming its own hub is a client choosing its own queue — inert with
 * one pilot hub, but a way around the fairness order the moment two bookable
 * hubs sit inside the 15 km ceiling (SECURITY §9a).
 *
 * The server now derives it from `serviceLocation` via `selectServingHub`,
 * which is the same rule the confirm schema below already stated for therapist,
 * price and slot: nothing in this body may steer the allocation.
 */
/** At most this many codes on one booking — MAX_THERAPIST_CODES_PER_BOOKING. */
const THERAPIST_CODES_MAX = 3;

const therapistCodesSchema = z
  .array(therapistCodeSchema)
  .min(1)
  .max(THERAPIST_CODES_MAX)
  .refine((codes) => new Set(codes).size === codes.length, {
    message: "Each Therapist Code can be used once per booking.",
  });

/**
 * The codes a request carries, whichever form it used: the list, the legacy
 * single code, or none. Every server path reads codes through this, so the two
 * forms cannot be handled two ways.
 */
export function requestedTherapistCodes(value: {
  therapistCode?: string;
  therapistCodes?: readonly string[];
}): string[] {
  if (value.therapistCodes) return [...value.therapistCodes];
  return value.therapistCode ? [value.therapistCode] : [];
}

export const asapAvailabilityRequestSchema = z
  .object({
    option: asapBookingOptionSchema,
    serviceLocation: z
      .object({
        lat: z.number().min(-90).max(90),
        lng: z.number().min(-180).max(180),
      })
      .strict(),
    /** One entry per paying client, in client-number order (Flow 1: 1–4). */
    clients: z
      .array(
        z
          .object({
            serviceVariantId: uuidSchema,
            /** True on BOTH clients of a Couple Massage Promo pair. */
            couplePromo: z.boolean().optional(),
            /**
             * Add-ons this client chose, by id. IDS ONLY — the server prices
             * them from its own catalog read, because a client that could send
             * a price could send zero (SECURITY §5).
             *
             * It belongs on the AVAILABILITY request too, not just the confirm:
             * the distance band's per-client minimum is checked against the
             * subtotal INCLUDING add-ons, so an availability call that ignored
             * them would clear a booking the confirm then refuses.
             */
            addOnIds: z.array(uuidSchema).max(10).optional(),
          })
          .strict(),
      )
      .min(1)
      .max(4),
    /**
     * Flow 4C — the CODE the client typed, never a staff id. The client is
     * never given an id (the lookup returns a first name and nothing else), so
     * the server resolves the code itself; there is deliberately no field here
     * for a tampered client to name a therapist with (SECURITY §9a).
     */
    therapistCode: therapistCodeSchema.optional(),
    /**
     * Up to three codes, one per therapist, in the order the client entered
     * them — Leon, 2026-10-02 (replaces CORE-01B §4.8.2's "exactly one").
     *
     * The single `therapistCode` above stays accepted because the production
     * app still sends it to this same backend; a request carries one form or
     * the other, never both. Read either through `requestedTherapistCodes`.
     */
    therapistCodes: therapistCodesSchema.optional(),
    /**
     * The client used **Remove** on the Engaged Favorite — CORE-01B §4.5
     * ("Client may remove an Engaged Favorite. The slot becomes open and
     * optional Add Therapist may appear"), TEST TA-002.
     *
     * ⚠ THE ONE PLACE CORE-01B IS SILENT — for Leon, not a default I chose.
     * Removal is specified as a display outcome, and every Continue reruns a
     * fresh live search (§2.2). Those two rules collide at confirm: the rerun
     * would re-apply "assign at most one earliest Engaged favorite" and hand
     * back the therapist the client just removed. This flag is the narrowest
     * thing that keeps the removal true through the rerun — it suppresses the
     * Engaged-favourite step and nothing else.
     *
     * It is NOT an exclusion list, deliberately: "a repeated search may return
     * the same Therapist if she remains the correct live result" (Add and
     * Change Therapist Rules), so the client cannot name who to skip.
     */
    declineEngagedFavorite: z.boolean().optional(),
  })
  .strict()
  .superRefine(refineBookingOptionAndPromo);

/**
 * POST /bookings/asap/confirm (ASAP Flows 10, 12). Same body as the advisory
 * read plus how the remainder settles — the server re-runs the ENTIRE
 * evaluation rather than trusting a candidate the client sends back, so there
 * is deliberately no therapist, price or slot field here to tamper with.
 */
/**
 * What the client must send at CONFIRM that it does not send at availability.
 *
 * The availability read is anonymous-shaped — a pin and some service ids. These
 * two only exist once there is a real visit to deliver, and both are for the
 * RIDER: they are the only fields on the booking that answer "where is the door
 * and how do I get in".
 *
 * ⚠ Neither steers allocation. `geo` is what the server evaluates and routes to;
 * these are what a human reads on arrival. A client that lies here inconveniences
 * only itself, which is why free text is acceptable when `hubId` was not.
 */
/**
 * The two cross-cutting body rules, shared by every booking schema so an
 * unwrapped one cannot lose them silently:
 *
 *   1. A therapist may be requested ONLY under `therapist_code`, and only ever
 *      by CODE — SECURITY §9a. Every other option naming one is a queue bypass,
 *      and no option may name a staff id at all.
 *   2. A Couple Massage Promo covers exactly two clients — one or three is a
 *      malformed booking, not a smaller promo (Flow 8P).
 */
function refineBookingOptionAndPromo(
  value: {
    option: string;
    therapistCode?: string;
    therapistCodes?: readonly string[];
    declineEngagedFavorite?: boolean;
    clients: readonly { couplePromo?: boolean }[];
  },
  ctx: z.RefinementCtx,
): void {
  const needsTherapist = value.option === "therapist_code";
  const codeCount = requestedTherapistCodes(value).length;
  if (value.therapistCode && value.therapistCodes) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["therapistCodes"],
      message: "Send therapistCodes or therapistCode, not both.",
    });
  }
  if (needsTherapist && codeCount === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["therapistCode"],
      message: "Therapist Code bookings must carry the code.",
    });
  }
  if (!needsTherapist && codeCount > 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["therapistCode"],
      message: "Only a Therapist Code booking may request a therapist.",
    });
  }
  const promoCount = value.clients.filter((c) => c.couplePromo).length;
  if (promoCount !== 0 && promoCount !== 2) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["clients"],
      message: "A Couple Massage Promo covers exactly two clients.",
    });
  }
  // Remove exists only on an Engaged Favorite, which only Favorites Only ever
  // assigns (CORE-01B §4.5). Under any other option the flag has no referent,
  // so it is a malformed body rather than a no-op.
  if (value.declineEngagedFavorite && value.option !== "favorites_only") {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["declineEngagedFavorite"],
      message: "Only a Favorites Only booking can remove an Engaged Favorite.",
    });
  }
}

const deliveryDetailsShape = {
  /**
   * GiftPasses this client already redeemed for THIS booking — `giftpasses.id`
   * values returned by `POST /giftpass/redeem`, never the display codes.
   *
   * Ids rather than codes because the redemption has already happened: the card
   * was consumed at scan time (`redeem_giftpass`, 0018, atomic by design so two
   * taps cannot spend one card twice), and confirm's job is to LINK it to the
   * booking it paid for — `link_giftpasses`, the half of 0018 that nothing ever
   * called, leaving passes redeemed with a NULL `redeemed_booking_id` and no
   * ledger row.
   *
   * ⚠ NOT TRUSTED HERE, AND IT DOES NOT NEED TO BE. `link_giftpasses` accepts a
   * pass only when it is `redeemed`, `redeemed_by = auth.uid()` and still
   * unlinked, and RAISES on any mismatch — so a borrowed or replayed id aborts
   * the booking rather than discounting it. The amounts are recomputed
   * server-side from the count that actually links.
   */
  giftpassPassIds: z.array(uuidSchema).max(20).optional(),
  /**
   * The address as the CLIENT wrote it — not a reverse-geocode of the pin.
   * `Mapbox` will happily name a building the client never mentioned; a rider at
   * a gate needs "Unit 4B, blue gate beside the sari-sari store".
   */
  addressText: z.string().trim().min(1).max(500).optional(),
  /**
   * The `saved_addresses` row this booking was placed against, when there is
   * one — `bookings.address_id`, an FK that has existed since 0005 and that no
   * client ever populated, leaving every booking unlinked from its address.
   *
   * Advisory and non-steering, like `addressText` beside it: the rider still
   * navigates to `serviceLocation`, and the server still writes `address_text`
   * from what was sent. It is a provenance link, so the structured parts 0088
   * added to `saved_addresses` can be recovered for a booking by admin, who is
   * the only role besides the owning client with a grant on that table.
   *
   * The client sends it ONLY while the draft still matches the saved row —
   * editing any field clears it — so the link never claims a booking went to an
   * address it did not. RLS is what makes trusting it safe: `addresses_own`
   * scopes the row to `auth.uid()`, so a tampered id cannot name someone
   * else's address.
   */
  addressId: z.string().uuid().optional(),
  /**
   * Access instructions for this visit (0057). Per-VISIT, not per-address:
   * "buzzer broken today" belongs to one booking, not to the home forever.
   *
   * ⚠ Client PII — gate codes and descriptions of the home. Never surface it to
   * therapist or hub_manager, and keep it out of logs (rules/security.md).
   */
  notesToRider: z.string().trim().max(1000).optional(),
} as const;

export const asapConfirmRequestSchema = asapAvailabilityRequestSchema
  .innerType()
  .extend({ paymentMethod: z.enum(["online", "cash"]), ...deliveryDetailsShape })
  .strict()
  .superRefine(refineBookingOptionAndPromo);

/**
 * POST /bookings/asap/waitlist/join (0393). Exactly the Book Now search the
 * client just ran: the server re-runs it while they wait, so it must be the same
 * validated shape. No payment method — they pay after a therapist is found,
 * through /asap/confirm.
 */
export const waitlistJoinRequestSchema = asapAvailabilityRequestSchema;

/** POST /bookings/asap/waitlist/status and /leave (0393): the client is the JWT. */
export const waitlistEmptyRequestSchema = z.object({}).strict();

/** Scheduled Flow 4's four Booking Options. */
export const scheduledBookingOptionSchema = z.enum([
  "favorites_first",
  "favorites_only",
  "therapist_code",
  "any_available",
]);

/**
 * POST /bookings/scheduled/assignment (Flows 4 -> 8). The client sends the
 * window it already chose and the Booking Option; it sends NO therapist, price
 * or plan, because the server re-derives all three. `clientId` is absent for the
 * same reason it is absent from the ASAP contract: favorites are resolved
 * server-side and a spoofed id would forge them (rules/security.md).
 */
export const scheduledAssignmentRequestSchema = z
  .object({
    // No hubId — server-derived (0056). See the availability schema above.
    option: scheduledBookingOptionSchema,
    date: manilaDateSchema,
    startHour: z.number().int().min(0).max(23),
    serviceLocation: z
      .object({
        lat: z.number().min(-90).max(90),
        lng: z.number().min(-180).max(180),
      })
      .strict(),
    clients: z
      .array(
        z
          .object({
            serviceVariantId: uuidSchema,
            couplePromo: z.boolean().optional(),
            /** Ids only — priced server-side. See the ASAP schema above. */
            addOnIds: z.array(uuidSchema).max(10).optional(),
          })
          .strict(),
      )
      .min(1)
      .max(4),
    /** Flow 6C — the CODE, never a staff id. See the ASAP schema above. */
    therapistCode: therapistCodeSchema.optional(),
    /** Up to three codes — see the ASAP schema above. */
    therapistCodes: therapistCodesSchema.optional(),
  })
  .strict()
  .superRefine(refineBookingOptionAndPromo);

/** One displayable window. Unavailable windows are never returned (Flow 2B). */
export const scheduledSlotAvailabilitySchema = z
  .object({
    /**
     * Manila start hour of the fixed one-hour window.
     *
     * ⚠ The bound is 0–23, not 14–23: the first bookable hour is
     * `operating_settings.booking_start_hour` since 0243, so a static floor
     * here would contradict the setting. THIS IS NOT A WEAKER GUARD — the
     * assignment path looks the requested hour up in the windows the server
     * just enumerated (`slots.find(s => s.startHour === input.startHour)`) and
     * returns `slot_unavailable` on a miss. The generated list is the control;
     * this bound only rejects a value that is not an hour at all.
     */
    startHour: z.number().int().min(0).max(23),
    startsAt: offsetDateTimeSchema,
    endsAt: offsetDateTimeSchema,
    /**
     * Flow 2B: "save earliest possible estimated start within the window".
     * Flow 3 §7 — an ESTIMATE. The exact session start happens when the
     * therapist starts the service, never from this value.
     */
    earliestStartAt: offsetDateTimeSchema,
  })
  .strict();

/**
 * A date option. Flow 2/2A/2B are emphatic that a date with no available slot
 * is shown as "Fully Booked" and left visible but unselectable — it is never
 * dropped from the list, so all four always come back.
 */
export const scheduledDateAvailabilitySchema = z
  .object({
    date: manilaDateSchema,
    /** 0 = Today, 1 = Tomorrow, 2 = Day After, 3 = Fourth Day. */
    offsetDays: z.number().int().min(0).max(3),
    status: z.enum(["available", "fully_booked"]),
    slots: z.array(scheduledSlotAvailabilitySchema),
  })
  .strict()
  .superRefine((option, context) => {
    if ((option.status === "available") !== option.slots.length > 0) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["status"],
        message: "A date is available exactly when it has at least one slot",
      });
    }
  });

/**
 * The advisory envelope from CAP-01, carried verbatim: an availability read
 * RESERVES NOTHING. `evaluatedAt`/`advisoryToken`/`capacityVersion` exist so
 * Flow 5's revalidation before payment can be correlated with what was shown.
 *
 * Deliberately PII-free in BOTH directions: no client contact data, and no
 * staff ids or counts either. The client is told which windows can be served,
 * not who would serve them — that is Flow 6's assignment, after a Booking
 * Option is chosen.
 */
export const scheduledAvailabilityResultSchema = z
  .object({
    bookingKind: z.literal("scheduled"),
    hubId: uuidSchema,
    evaluatedAt: offsetDateTimeSchema,
    advisoryToken: z.string().min(1).max(200),
    capacityVersion: z.string().min(1).max(200),
    dates: z.array(scheduledDateAvailabilitySchema).length(4),
    /** Request-level findings (hours, service area, routing) — never per staff. */
    blockers: z.array(capacityBlockerSchema),
  })
  .strict()
  .superRefine((result, context) => {
    if (result.blockers.some((blocker) => blocker.staffId !== undefined)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["blockers"],
        message: "A client-facing availability result must not name staff",
      });
    }
  });

export type CapacityBookingKind = z.infer<typeof capacityBookingKindSchema>;
export type CapacityTimeWindow = z.infer<typeof capacityTimeWindowSchema>;
export type CapacityEvaluationInput = z.infer<typeof capacityEvaluationInputSchema>;
export type CapacityEvaluationContext = z.infer<typeof capacityEvaluationContextSchema>;
export type TherapistQualificationSignal = z.infer<typeof therapistQualificationSignalSchema>;
export type TherapistEligibilityInput = z.infer<typeof therapistEligibilityInputSchema>;
export type CapacityBlockerCode = z.infer<typeof capacityBlockerCodeSchema>;
export type CapacityBlockerSubject = z.infer<typeof capacityBlockerSubjectSchema>;
export type CapacityBlocker = z.infer<typeof capacityBlockerSchema>;
export type TherapistEligibilityResult = z.infer<typeof therapistEligibilityResultSchema>;
export type CapacityPlanSummary = z.infer<typeof capacityPlanSummarySchema>;
export type AdvisoryAvailabilityResult = z.infer<typeof advisoryAvailabilityResultSchema>;
export type ScheduledAvailabilityRequest = z.infer<typeof scheduledAvailabilityRequestSchema>;
export type AsapBookingOptionContract = z.infer<typeof asapBookingOptionSchema>;
export type AsapAvailabilityRequestContract = z.infer<typeof asapAvailabilityRequestSchema>;
export type AsapConfirmRequestContract = z.infer<typeof asapConfirmRequestSchema>;
export type WaitlistJoinRequestContract = z.infer<typeof waitlistJoinRequestSchema>;
/**
 * The Scheduled confirm body. Separate from the assignment contract because it
 * additionally carries the delivery details — the route used to type its input
 * as the assignment contract, so `addressText`, `notesToRider` and now
 * `addressId` were validated at runtime but invisible to the compiler, which is
 * how a field can be dropped from a handler without anything failing to build.
 */
export type ScheduledConfirmRequestContract = z.infer<typeof scheduledConfirmRequestSchema>;
export type ScheduledBookingOptionContract = z.infer<typeof scheduledBookingOptionSchema>;
/**
 * POST /bookings/scheduled/confirm (Flows 15/16). The assignment body plus the
 * two delivery details: the server re-derives the therapists, the plan and
 * every amount, so there is still nothing here that steers the outcome. Payment
 * method is absent because Scheduled is cashless by rule (OQ-22, QRPh only).
 *
 * `addressText`/`notesToRider` are for the rider at the door, not the engine —
 * see `deliveryDetailsShape`. They belong to confirm rather than assignment for
 * the same reason `paymentMethod` does on the ASAP side: the advisory read has
 * no visit to deliver yet.
 */
export const scheduledConfirmRequestSchema = scheduledAssignmentRequestSchema
  // `.innerType()` because the assignment schema is a ZodEffects — and that is
  // load-bearing: unwrapping DROPS its superRefine, which is what enforces
  // "only a Therapist Code booking may name a therapist" (SECURITY §9a). It is
  // re-applied below rather than lost. The same trick on the ASAP side re-applies
  // its own copy for the same reason.
  .innerType()
  .extend(deliveryDetailsShape)
  .strict()
  .superRefine(refineBookingOptionAndPromo);

/**
 * POST /bookings/scheduled/cancel (Flow 18). The group id is the only input:
 * a multi-therapist booking is several rows sharing one `booking_group_id`, and
 * cancelling a single row would strand both its siblings and their capacity.
 *
 * There is no refund field. Who may authorise a refund is undefined (OQ-16), so
 * the capability is absent rather than present-and-gated — `refund_status`
 * stays `none` (never-invent-policy.md).
 */
export const scheduledCancelRequestSchema = z
  .object({
    bookingGroupId: uuidSchema,
    /** Free text kept for the audit trail; never shown to staff. */
    reason: z.string().trim().max(500).optional(),
  })
  .strict();

export type ScheduledCancelRequestContract = z.infer<typeof scheduledCancelRequestSchema>;
export type ScheduledAssignmentRequestContract = z.infer<typeof scheduledAssignmentRequestSchema>;
export type ScheduledSlotAvailability = z.infer<typeof scheduledSlotAvailabilitySchema>;
export type ScheduledDateAvailability = z.infer<typeof scheduledDateAvailabilitySchema>;
export type ScheduledAvailabilityResult = z.infer<typeof scheduledAvailabilityResultSchema>;
